import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PublicLayout } from '../components/PublicLayout';
import SEO, { SEOConfig } from '../components/SEO';
import { Home as HomeIcon, Users, Building2, Wrench, Briefcase, ArrowRight, Check, Zap, Shield, Clock, FileText, Camera, DollarSign, MessageCircle, BarChart3 } from 'lucide-react';
import { Card } from '../components/ui/card';

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      if (user.role === 'tenant') {
        navigate('/tenant/dashboard');
      } else if (user.role === 'landlord' || user.role === 'landlord_admin') {
        navigate('/landlord/dashboard');
      } else if (user.role === 'agent') {
        navigate('/agent/dashboard');
      } else if (user.role === 'contractor') {
        navigate('/contractor/dashboard');
      }
    }
  }, [user, navigate]);

  const audiences = [
    {
      href: '/tenants',
      icon: Users,
      title: "I'm a Tenant",
      description: 'Find your perfect home with a reusable profile',
      color: 'blue'
    },
    {
      href: '/landlords',
      icon: Building2,
      title: "I'm a Landlord",
      description: 'Manage properties at just 6% - no hidden fees',
      color: 'green',
      badge: '6% FULL MGMT'
    },
    {
      href: '/contractors',
      icon: Wrench,
      title: "I'm a Contractor",
      description: 'Join our network and grow your business',
      color: 'orange'
    },
    {
      href: '/agents',
      icon: Briefcase,
      title: "I'm an Agent",
      description: 'Scale your agency with better technology',
      color: 'purple'
    }
  ];

  const features = [
    { icon: DollarSign, title: '6% Management Fee', description: 'Lower than traditional agents with better technology' },
    { icon: Shield, title: 'Full Compliance', description: 'Certificates tracked, reminders sent automatically' },
    { icon: Zap, title: 'Smart Automation', description: 'Applications, repairs, and payments - all streamlined' },
    { icon: Clock, title: 'Faster Lettings', description: 'Smart matching technology reduces void periods' },
    { icon: Camera, title: 'Smart Inspections', description: 'Video walkthroughs with intelligent comparison reports' },
    { icon: BarChart3, title: 'Full Transparency', description: 'Every action logged with complete audit trail' }
  ];

  return (
    <PublicLayout>
      <SEO {...SEOConfig.home} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 via-white to-slate-100 py-12 sm:py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-extrabold tracking-tight leading-[1.1] mb-4 sm:mb-6">
            LetWise
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl text-primary font-semibold mb-6">
            Letting done properly
          </p>
          <p className="text-base sm:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto mb-8 sm:mb-12">
            A modern platform that reduces costs, improves transparency, and automates property management, inspections, and repairs.
          </p>

          {/* Audience Selection */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 max-w-5xl mx-auto">
            {audiences.map((audience) => {
              const colorClasses = {
                blue: 'bg-blue-100 group-hover:bg-blue-500 text-blue-600 group-hover:text-white',
                green: 'bg-green-100 group-hover:bg-green-500 text-green-600 group-hover:text-white',
                orange: 'bg-orange-100 group-hover:bg-orange-500 text-orange-600 group-hover:text-white',
                purple: 'bg-purple-100 group-hover:bg-purple-500 text-purple-600 group-hover:text-white'
              };

              return (
                <Link key={audience.href} to={audience.href} className="group">
                  <Card className="p-4 sm:p-6 lg:p-8 text-center hover:shadow-xl hover:border-primary transition-all duration-300 cursor-pointer h-full relative overflow-hidden">
                    {audience.badge && (
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-bl-lg">
                        {audience.badge}
                      </div>
                    )}
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 mx-auto mb-3 sm:mb-4 lg:mb-6 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all ${colorClasses[audience.color]}`}>
                      <audience.icon className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
                    </div>
                    <h3 className="text-sm sm:text-lg lg:text-2xl font-display font-bold mb-1 sm:mb-2 lg:mb-3">{audience.title}</h3>
                    <p className="text-xs sm:text-sm text-slate-600 mb-2 sm:mb-4 hidden sm:block">{audience.description}</p>
                    <span className="inline-flex items-center text-primary font-semibold text-xs sm:text-sm">
                      Learn More <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-center mb-8 sm:mb-12">
            Why Choose LetWise?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex gap-4 p-4 sm:p-6 rounded-xl border hover:shadow-md transition-shadow">
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg mb-1">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 lg:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-center mb-8 sm:mb-12">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { step: '1', title: 'Create Account', description: 'Sign up free as a landlord, tenant, or contractor. Takes less than 2 minutes.' },
              { step: '2', title: 'Set Up', description: 'Landlords add properties. Tenants create profiles. Contractors join our network.' },
              { step: '3', title: 'Get Started', description: 'List properties, apply for homes, or start receiving jobs. We handle the rest.' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-primary text-white flex items-center justify-center text-xl sm:text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-bold text-lg sm:text-xl mb-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-base sm:text-lg opacity-90 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Join thousands of landlords, tenants, and contractors using LetWise to simplify lettings.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold bg-white text-primary rounded-xl hover:bg-slate-100 transition-colors w-full sm:w-auto"
              data-testid="cta-create-account"
            >
              Create Free Account
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/properties"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold border-2 border-white/30 rounded-xl hover:bg-white/10 transition-colors w-full sm:w-auto"
              data-testid="cta-browse-properties"
            >
              Browse Properties
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
